import {
  savedFilterListSchema,
  savedFilterSchema,
  type SavedFilter,
  type SavedFilterCreate,
  type SavedFilterUpdate,
} from '@xphoto/shared';
import { ApiError } from './api';
import { apiFetch } from '../lib/http';

/**
 * Client typé des FILTRES ENREGISTRÉS (« filtres enregistrés + expression booléenne »).
 * Consomme le backend `saved-filters` et RE-PARSE chaque réponse via son schéma partagé
 * `@xphoto/shared` (« never trust the client », y compris ses propres réponses). Les
 * erreurs sont propagées en `ApiError` (statut + code) — indispensable pour DISCRIMINER
 * le 409 `CONFLICT` (nom déjà pris) lors d'une création/renommage, sur le patron
 * `taxonomy-api.ts`.
 */

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) {
    let code = 'UNKNOWN';
    try {
      const body = (await res.json()) as { error?: { code?: string } };
      if (typeof body?.error?.code === 'string') code = body.error.code;
    } catch {
      // Corps non-JSON : on garde le code générique.
    }
    throw new ApiError(res.status, code);
  }
  return res.json();
}

/** `GET /api/libraries/:id/saved-filters` — la liste ordonnée (par `position`). */
export async function fetchSavedFilters(
  libraryId: string,
  signal?: AbortSignal,
): Promise<SavedFilter[]> {
  const res = await apiFetch(`/api/libraries/${libraryId}/saved-filters`, {
    signal: signal ?? null,
  });
  return savedFilterListSchema.parse(await readJson(res)).filters;
}

/**
 * `POST /api/libraries/:id/saved-filters` — crée un filtre. 409 `CONFLICT` si le nom
 * existe déjà, 400 `VALIDATION` si l'expression ne parse pas (rejeté côté route).
 */
export async function createSavedFilter(
  libraryId: string,
  body: SavedFilterCreate,
): Promise<SavedFilter> {
  const res = await apiFetch(`/api/libraries/${libraryId}/saved-filters`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return savedFilterSchema.parse(await readJson(res));
}

/**
 * `PATCH /api/libraries/:id/saved-filters/:filterId` — met à jour un filtre (partiel).
 * `icon: null` RETIRE l'icône (distinct de l'absence de champ). 409 si renommage en doublon.
 */
export async function updateSavedFilter(
  libraryId: string,
  filterId: string,
  patch: SavedFilterUpdate,
): Promise<SavedFilter> {
  const res = await apiFetch(`/api/libraries/${libraryId}/saved-filters/${filterId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return savedFilterSchema.parse(await readJson(res));
}

/** `DELETE /api/libraries/:id/saved-filters/:filterId` — supprime un filtre (204). */
export async function deleteSavedFilter(libraryId: string, filterId: string): Promise<void> {
  const res = await apiFetch(`/api/libraries/${libraryId}/saved-filters/${filterId}`, {
    method: 'DELETE',
  });
  if (!res.ok) await readJson(res); // lève une ApiError typée
}

/**
 * `PUT /api/libraries/:id/saved-filters/order` — réordonne : le rang de chaque filtre =
 * son index dans `ids`. Renvoie la liste re-ordonnée (re-parsée).
 */
export async function reorderSavedFilters(
  libraryId: string,
  ids: string[],
): Promise<SavedFilter[]> {
  const res = await apiFetch(`/api/libraries/${libraryId}/saved-filters/order`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  return savedFilterListSchema.parse(await readJson(res)).filters;
}
